import PageDivider, { PageDividerProps } from "@/Components/UI/PageDivider";
import { CookieManagerContext } from "@/Contexts/CookieManagerContext";
import { homePageFor } from "@/utils/homePage";
import { useIdentity } from "@/utils/identity";
import { Link } from "@/utils/navigation";
import { useCoachApplicationPageExperiment } from "@/utils/useCoachApplicationPageExperiment";
import { useContext } from "react";
import { createPortal } from "react-dom";
import { FaFacebookF, FaInstagram } from "react-icons/fa";

function Disclaimer({
    topBgColor,
    showDivider,
}: PageDividerProps & {
    showDivider: boolean;
}) {
    return (
        <>
            {showDivider && (
                <div className="self-stretch">
                    <PageDivider topBgColor={topBgColor} />
                </div>
            )}
            <div className="flex w-full justify-center bg-brand-lightGray p-4">
                <div className="max-w-7xl text-sm text-brand-black">
                    {import.meta.env.VITE_APP_NAME} does not facilitate
                    healthcare services. While some coaches may also be
                    healthcare professionals, they do not act in this capacity
                    on {import.meta.env.VITE_APP_NAME}. Coaches will not offer
                    any diagnosis, treatment, prescribing of medication, or any
                    other clinical services. For mental health disorders or
                    concerns, please seek help from a licensed psychotherapist
                    or psychiatrist.
                </div>
            </div>
        </>
    );
}

type FooterLink = { text: string } & (
    | { href: string }
    | { onClick: () => void }
);

const FOOTER_LINK_CLASSES =
    "cursor-pointer text-sm leading-6 text-brand-white hover:underline text-left";

export type FooterProps = {
    disclaimerPortalElem?: HTMLElement | null;
    topBgColor?: string;
    showDivider?: boolean;
    showICFSponsorBadgeOnFooter?: boolean;
};

export default function Footer({
    disclaimerPortalElem,
    topBgColor,
    showDivider = true,
    showICFSponsorBadgeOnFooter = true,
}: FooterProps) {
    const cookieContext = useContext(CookieManagerContext);

    const { user } = useIdentity();

    const { openCoachApplyQuiz } = useCoachApplicationPageExperiment();

    const footerLinks: FooterLink[] = [
        { text: "FAQ", href: route("faq") },
        { text: "About Us", href: route("about") },
        { text: "Reviews", href: route("reviews") },
        { text: "Contact Us", href: route("contact") },
        { text: "Terms of Service", href: route("terms") },
        { text: "Privacy Policy", href: route("privacy") },
        {
            text: "Sharing Settings",
            onClick: () => cookieContext.setShowCookieSettingsModal(true),
        },
        { text: "Resources", href: route("archetypes") },
        { text: "Apply as a Coach", onClick: () => openCoachApplyQuiz() },
    ];

    return (
        <div
            className="self-stretch bg-brand-gray print:hidden"
            aria-labelledby="footer-heading"
        >
            {disclaimerPortalElem ? (
                createPortal(
                    <Disclaimer
                        topBgColor={topBgColor ?? "white"}
                        showDivider={showDivider}
                    />,
                    disclaimerPortalElem,
                    "disclaimer",
                )
            ) : (
                <Disclaimer
                    topBgColor={topBgColor ?? "white"}
                    showDivider={showDivider}
                />
            )}
            <div
                data-testid="footer-section"
                className="mx-auto max-w-7xl px-6 pb-8 pt-12 lg:px-8"
            >
                <Link data-testid="sb-logo-footer" href={homePageFor(user)}>
                    <img
                        src={`${import.meta.env.VITE_CDN_URL}/images/logo.webp`}
                        alt="icon"
                        className="mb-8 mr-2 h-8 sm:mr-6"
                    />
                </Link>
                <div className="mt-4 flex grow flex-col gap-x-6 gap-y-3 md:flex-row md:items-center lg:gap-8">
                    {footerLinks.map((footerLink) =>
                        "href" in footerLink ? (
                            <Link
                                key={footerLink.text}
                                href={footerLink.href}
                                data-testid="site-info-links"
                                className={FOOTER_LINK_CLASSES}
                            >
                                {footerLink.text}
                            </Link>
                        ) : (
                            <button
                                key={footerLink.text}
                                data-testid="site-info-links"
                                className={FOOTER_LINK_CLASSES}
                                onClick={footerLink.onClick}
                                type="button"
                            >
                                {footerLink.text}
                            </button>
                        ),
                    )}
                </div>
                <div className="mt-8 border-t border-gray-100 pt-8 sm:flex sm:items-center sm:justify-between">
                    <div className="flex grow space-x-4 sm:order-2 sm:justify-end items-center">
                        {showICFSponsorBadgeOnFooter && (
                            <img
                                src={`${import.meta.env.VITE_CDN_URL}/images/ICF-sponsor-badge.webp`}
                                alt="ICF Sponsor Badge"
                                className="h-15 -my-10"
                            />
                        )}
                        {[
                            {
                                elem: FaFacebookF,
                                link: "https://www.facebook.com/people/Strawberryme/61560914316975/",
                            },
                            {
                                elem: FaInstagram,
                                link: "https://www.instagram.com/strawberrydotme/",
                            },
                        ].map((icon) => (
                            <a
                                data-testid="social-media-icons"
                                key={icon.link}
                                href={icon.link}
                                target={"_blank"}
                            >
                                <div className="cursor-pointer rounded-full bg-brand-white p-1.5 text-brand-black hover:bg-brand-border">
                                    <icon.elem className="" />
                                </div>
                            </a>
                        ))}
                    </div>
                    <div className="mt-8 sm:order-1 sm:mt-0">
                        <p
                            data-testid="current-year"
                            className="text-sm text-brand-white"
                        >
                            &copy; {new Date().getFullYear()}{" "}
                            {import.meta.env.VITE_APP_NAME}. Made with ❤️ and 🍓
                            in Palo Alto, California.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
